import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { refundMolliePayment } from "@/services/mollieService";
import {
  CreditCard,
  X,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Loader2,
  Search,
  Filter,
  Calendar,
  Edit,
} from "lucide-react";

interface Payment {
  id: string;
  user_id: string;
  mollie_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  metadata: any;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  user?: {
    display_name: string;
    studio_name?: string | null;
    premium_end_date?: string | null;
  };
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  premium_monthly: "Premium Subscription (Monthly)",
  premium_yearly: "Premium Subscription (Yearly)",
  level2_boost: "Level 2 Boost",
  level3_boost: "Level 3 Boost",
  level4_boost: "Level 4 Boost",
  twitter_post: "Twitter Post Promotion",
  newsletter_basic: "Newsletter Basic Promotion",
  press_kit: "Press Kit Auto-Builder",
  creator_push: "Creator Push Promotion",
};

interface PaymentsManagementProps {
  userId?: string; // If provided, only show payments for this user
  isAdmin?: boolean; // If true, allow instant cancellation and date modification
  onPaymentUpdate?: () => void; // Callback when a payment is updated
}

export function PaymentsManagement({ userId, isAdmin = false, onPaymentUpdate }: PaymentsManagementProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("paid");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditDateDialog, setShowEditDateDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState<Payment | null>(null);
  const [paymentToEdit, setPaymentToEdit] = useState<Payment | null>(null);
  const [paymentToRefund, setPaymentToRefund] = useState<Payment | null>(null);
  const [newEndDate, setNewEndDate] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [users, setUsers] = useState<Array<{ id: string; display_name: string; studio_name?: string | null }>>([]);

  useEffect(() => {
    fetchPayments();
    if (isAdmin && !userId) {
      fetchUsers();
    }
  }, [userId, isAdmin]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, studio_name")
        .eq("user_type", "developer")
        .order("display_name", { ascending: true });

      if (error) {
        console.error("Error fetching users:", error);
      } else {
        setUsers(data || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("payments")
        .select(`
          *,
          user:profiles!payments_user_id_fkey(
            display_name,
            studio_name,
            premium_end_date
          )
        `)
        .neq("status", "pending")
        .neq("status", "open")
        .order("created_at", { ascending: false });

      if (userId) {
        query = query.eq("user_id", userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching payments:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        toast.error(`Failed to load payments: ${error.message}`);
        setPayments([]);
      } else {
        console.log("Payments fetched:", data?.length || 0);
        setPayments(data || []);
      }
    } catch (error: any) {
      console.error("Error fetching payments:", error);
      toast.error(`Failed to load payments: ${error?.message || 'Unknown error'}`);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case "pending":
      case "open":
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "failed":
      case "expired":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case "canceled":
        return <Badge variant="secondary"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amount);
  };

  const getUniquePaymentTypes = () => {
    const types = new Set(payments.map(p => p.payment_type));
    return Array.from(types);
  };

  // Filter payments
  const filteredPayments = payments.filter((payment) => {
    // Status filter
    if (statusFilter !== "all" && payment.status !== statusFilter) {
      return false;
    }

    // Type filter
    if (typeFilter !== "all" && payment.payment_type !== typeFilter) {
      return false;
    }

    // User filter (admin only)
    if (isAdmin && !userId && userFilter !== "all") {
      if (payment.user_id !== userFilter) {
        return false;
      }
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const typeLabel = PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type;
      const amount = formatAmount(payment.amount, payment.currency);
      const userName = payment.user?.display_name || payment.user?.studio_name || "";
      if (
        !typeLabel.toLowerCase().includes(query) &&
        !amount.toLowerCase().includes(query) &&
        !payment.mollie_payment_id.toLowerCase().includes(query) &&
        !userName.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    return true;
  });

  const handleCancelClick = (payment: Payment) => {
    setPaymentToCancel(payment);
    setShowCancelDialog(true);
  };

  const handleCancelPayment = async () => {
    if (!paymentToCancel) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from("payments")
        .update({ status: "canceled" })
        .eq("id", paymentToCancel.id);

      if (error) {
        throw error;
      }

      // Handle different payment types
      if (paymentToCancel.payment_type.startsWith("premium_") && paymentToCancel.status === "paid") {
        // Premium subscription: revoke premium access
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            is_premium: false,
            premium_end_date: new Date().toISOString(),
          })
          .eq("id", paymentToCancel.user_id);

        if (profileError) {
          console.error("Error updating profile:", profileError);
          throw profileError;
        }
      } else if (paymentToCancel.payment_type.startsWith("level") && paymentToCancel.payment_type.endsWith("_boost")) {
        // Level boost: remove highlight from game
        let metadata = paymentToCancel.metadata;
        if (typeof metadata === 'string') {
          try {
            metadata = JSON.parse(metadata);
          } catch (e) {
            console.error("Error parsing metadata:", e);
            metadata = {};
          }
        }
        const gameId = metadata?.game_id;
        if (gameId) {
          const { error: gameError } = await supabase
            .from("games")
            .update({
              highlight_id: 1, // Reset to default
              highlight_end_date: null,
            })
            .eq("id", gameId)
            .eq("developer_id", paymentToCancel.user_id);

          if (gameError) {
            console.error("Error updating game highlight:", gameError);
            throw gameError;
          }
        }
      }
      // Note: press_kit cannot be cancelled (handled in UI)

      toast.success("Payment cancelled successfully");
      setShowCancelDialog(false);
      setPaymentToCancel(null);
      await fetchPayments();
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (error: any) {
      console.error("Error cancelling payment:", error);
      toast.error(error.message || "Failed to cancel payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleRefundClick = (payment: Payment) => {
    setPaymentToRefund(payment);
    setShowRefundDialog(true);
  };

  const handleRefundPayment = async () => {
    if (!paymentToRefund) return;

    // Check if payment is in a refundable state
    if (paymentToRefund.status !== "paid") {
      toast.error("Only paid payments can be refunded");
      return;
    }

    setProcessing(true);
    try {
      console.log("Processing refund for payment:", {
        molliePaymentId: paymentToRefund.mollie_payment_id,
        amount: paymentToRefund.amount,
        status: paymentToRefund.status
      });

      // Process refund via Mollie API
      const refundResult = await refundMolliePayment(
        paymentToRefund.mollie_payment_id,
        paymentToRefund.amount // Full refund by default
      );

      console.log("Refund result:", refundResult);

      if (!refundResult.success) {
        throw new Error(refundResult.error || "Failed to process refund");
      }

      toast.success(`Payment refunded successfully via Mollie. Refund ID: ${refundResult.refundId || 'N/A'}`);
      setShowRefundDialog(false);
      setPaymentToRefund(null);
      await fetchPayments();
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (error: any) {
      console.error("Error refunding payment:", error);
      toast.error(error.message || "Failed to refund payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleEditDateClick = async (payment: Payment) => {
    setPaymentToEdit(payment);

    // Fetch current premium_end_date from user profile
    if (payment.payment_type.startsWith("premium_")) {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("premium_end_date")
          .eq("id", payment.user_id)
          .single();

        if (!error && data?.premium_end_date) {
          const endDate = new Date(data.premium_end_date);
          setNewEndDate(endDate.toISOString().split('T')[0]);
        } else {
          // Fallback: calculate from paid_at
          if (payment.paid_at) {
            const currentDate = new Date(payment.paid_at);
            if (payment.payment_type === "premium_monthly") {
              currentDate.setMonth(currentDate.getMonth() + 1);
            } else {
              currentDate.setFullYear(currentDate.getFullYear() + 1);
            }
            setNewEndDate(currentDate.toISOString().split('T')[0]);
          } else {
            setNewEndDate("");
          }
        }
      } catch (error) {
        console.error("Error fetching premium_end_date:", error);
        setNewEndDate("");
      }
    } else {
      setNewEndDate("");
    }

    setShowEditDateDialog(true);
  };

  const handleUpdateDate = async () => {
    if (!paymentToEdit || !newEndDate) return;

    setProcessing(true);
    try {
      // Update premium_end_date in profiles table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          premium_end_date: new Date(newEndDate).toISOString(),
        })
        .eq("id", paymentToEdit.user_id);

      if (profileError) {
        throw profileError;
      }

      toast.success("Subscription end date updated successfully");
      setShowEditDateDialog(false);
      setPaymentToEdit(null);
      setNewEndDate("");
      await fetchPayments();
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (error: any) {
      console.error("Error updating date:", error);
      toast.error(error.message || "Failed to update date");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className={`grid grid-cols-1 md:grid-cols-${isAdmin && !userId ? '5' : '4'} gap-4`}>
            <div>
              <Label htmlFor="search" className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  id="search"
                  placeholder="Search payments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
              </div>
            </div>
            {isAdmin && !userId && (
              <div>
                <Label htmlFor="user" className="text-xs">User</Label>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger id="user" className="h-9 text-sm mt-1">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.studio_name || user.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="status" className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="status" className="h-9 text-sm mt-1">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="type" className="text-xs">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger id="type" className="h-9 text-sm mt-1">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {getUniquePaymentTypes().map((type) => (
                    <SelectItem key={type} value={type}>
                      {PAYMENT_TYPE_LABELS[type] || type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStatusFilter("all");
                  setTypeFilter("all");
                  setSearchQuery("");
                  setUserFilter("all");
                }}
                className="h-9 w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      {filteredPayments.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    {isAdmin && !userId && (
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">User</th>
                    )}
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Amount</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Created</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Paid</th>
                    {filteredPayments.some(p => p.payment_type.startsWith("premium_") && p.status === "paid") && (
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">End Date</th>
                    )}
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-muted/30 transition-colors">
                      {isAdmin && !userId && (
                        <td className="p-3">
                          <span className="text-sm">
                            {payment.user?.studio_name || payment.user?.display_name || "Unknown"}
                          </span>
                        </td>
                      )}
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-semibold">
                          {formatAmount(payment.amount, payment.currency)}
                        </span>
                      </td>
                      <td className="p-3">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="p-3">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(payment.created_at)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs text-muted-foreground">
                          {payment.paid_at ? formatDate(payment.paid_at) : "-"}
                        </span>
                      </td>
                      {filteredPayments.some(p => p.payment_type.startsWith("premium_") && p.status === "paid") && (
                        <td className="p-3">
                          {payment.payment_type.startsWith("premium_") && payment.status === "paid" ? (
                            <span className="text-xs text-muted-foreground">
                              {payment.user?.premium_end_date
                                ? formatDate(payment.user.premium_end_date)
                                : payment.payment_type === "premium_monthly"
                                  ? formatDate(new Date(new Date(payment.paid_at || payment.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString())
                                  : formatDate(new Date(new Date(payment.paid_at || payment.created_at).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString())}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                      <td className="p-3">
                        <div className="flex justify-end gap-2">
                          {isAdmin && payment.payment_type.startsWith("premium_") && payment.status === "paid" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditDateClick(payment)}
                              disabled={processing}
                              className="h-7 text-xs"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Edit Date
                            </Button>
                          )}
                          {isAdmin && payment.payment_type === "press_kit" && payment.status === "paid" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefundClick(payment)}
                              disabled={processing}
                              className="h-7 text-xs"
                              title={`Refund payment ${payment.mollie_payment_id}`}
                            >
                              <CreditCard className="w-3 h-3 mr-1" />
                              Refund
                            </Button>
                          )}
                          {isAdmin &&
                            payment.payment_type !== "press_kit" &&
                            (payment.status === "paid" || payment.status === "pending" || payment.status === "open") && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleCancelClick(payment)}
                                disabled={processing}
                                className="h-7 text-xs"
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {payments.length === 0 ? "No payments found" : "No payments match your filters"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Cancel Payment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this payment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {paymentToCancel && (
            <div className="space-y-2">
              <p className="text-sm">
                <strong>Type:</strong> {PAYMENT_TYPE_LABELS[paymentToCancel.payment_type] || paymentToCancel.payment_type}
              </p>
              <p className="text-sm">
                <strong>Amount:</strong> {formatAmount(paymentToCancel.amount, paymentToCancel.currency)}
              </p>
              {paymentToCancel.payment_type.startsWith("premium_") && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Premium Subscription</AlertTitle>
                  <AlertDescription>
                    Cancelling this subscription will immediately revoke premium access for the user.
                  </AlertDescription>
                </Alert>
              )}
              {(paymentToCancel.payment_type.startsWith("level") && paymentToCancel.payment_type.endsWith("_boost")) && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Level Boost</AlertTitle>
                  <AlertDescription>
                    Cancelling this boost will remove the highlight from the associated game and reset it to default level.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setPaymentToCancel(null);
              }}
            >
              Keep Payment
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelPayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Cancellation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Refund Payment
            </DialogTitle>
            <DialogDescription>
              Process a refund for this payment. This will mark the payment as refunded.
            </DialogDescription>
          </DialogHeader>

          {paymentToRefund && (
            <div className="space-y-2">
              <p className="text-sm">
                <strong>Type:</strong> {PAYMENT_TYPE_LABELS[paymentToRefund.payment_type] || paymentToRefund.payment_type}
              </p>
              <p className="text-sm">
                <strong>Amount:</strong> {formatAmount(paymentToRefund.amount, paymentToRefund.currency)}
              </p>
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Refund Notice</AlertTitle>
                <AlertDescription>
                  This action will mark the payment as refunded. Make sure to process the actual refund through your payment provider (Mollie) separately.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRefundDialog(false);
                setPaymentToRefund(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleRefundPayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Refund"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Date Dialog */}
      <Dialog open={showEditDateDialog} onOpenChange={setShowEditDateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Edit Subscription End Date
            </DialogTitle>
            <DialogDescription>
              Update the premium subscription end date for this user.
            </DialogDescription>
          </DialogHeader>

          {paymentToEdit && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="endDate">Premium End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The user's premium access will end on this date.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDateDialog(false);
                setPaymentToEdit(null);
                setNewEndDate("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDate}
              disabled={processing || !newEndDate}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Update Date"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

